import React, { useState, useEffect } from "react";
import {
  Container,
  Chip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Collapse,
  useMediaQuery,
  Card,
  CardContent,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { fetchOrders, createOrder } from "../api/ordersApi";
import { getCustomers } from "../api/customerApi";
import { fetchProducts } from "../api/productApi";
import { getProductsForCustomer } from "../api/productApi";
import Grid from '@mui/material/Grid';
import axios from "axios";

const OrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [newOrderModalOpen, setNewOrderModalOpen] = useState(false);
  const [customers, setCustomers] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [expandedOrderId, setExpandedOrderId] = useState<number | null>(null);
  const isMobile = useMediaQuery("(max-width:600px)");

  const [newOrder, setNewOrder] = useState<any>({
    customerId: "",
    items: [{ productId: "", variantId: null, quantity: 1, price: 0 }],
    note: "",
    total: 0,
  });

  useEffect(() => {
    fetchAllOrders();
    fetchCustomers();
  }, []);

  const fetchAllOrders = async () => {
    try {
      const data = await fetchOrders();
      setOrders(data.map(order => ({
        ...order,
        order_status: order.order_status || "N/A",
        status: order.status || "Pending",
        fulfillment_status: order.fulfillment_status || "unfulfilled"
      })));
    } catch (error) {
      console.error("Error fetching orders:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const data = await getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const handleCustomerChange = async (customerId: number) => {
    setNewOrder((prevOrder: any) => ({
      ...prevOrder,
      customerId,
      items: [{ productId: "", variantId: null, quantity: 1, price: 0 }],
    }));
    await fetchProductsForSelectedCustomer(customerId);
  };

  const getCustomerName = (customerId: number) => {
    if (!customerId) return "Unknown";
    const customer = customers.find((cust: any) => cust.id === customerId);
    return customer ? customer.name : `Customer ID: ${customerId}`;
  };

  const handleAddItem = () => {
    setNewOrder((prevOrder: any) => ({
      ...prevOrder,
      items: [...prevOrder.items, { productId: "", variantId: null, quantity: 1, price: 0 }],
    }));
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = [...newOrder.items];
    updatedItems.splice(index, 1);
    setNewOrder((prevOrder: any) => ({
      ...prevOrder,
      items: updatedItems,
    }));
    calculateTotal(updatedItems);
  };

  const fetchProductsForSelectedCustomer = async (customerId: number) => {
    try {
      const data = await getProductsForCustomer(customerId);
      console.log("Fetched products with custom prices:", data);
      setProducts(data);
    } catch (error) {
      console.error("Error fetching products for customer:", error);
    }
  };

  const handleItemChange = (index: number, field: string, value: any) => {
    const updatedItems = [...newOrder.items];
    updatedItems[index][field] = value;

    if (field === "productId" && value) {
      const selectedProduct = products.find((product: any) => product.product_id === value);
      updatedItems[index].variantId = null;
      updatedItems[index].price =
        selectedProduct?.custom_price ?? selectedProduct?.base_price ?? 0;
    }

    if (field === "variantId" && value) {
      const selectedProduct = products.find((product: any) =>
        product.variants.some((variant: any) => variant.id === value)
      );
      const selectedVariant = selectedProduct?.variants.find((variant: any) => variant.id === value);
      updatedItems[index].price =
        selectedVariant?.custom_price ?? selectedVariant?.price ?? 0;
    }

    setNewOrder((prevOrder: any) => ({
      ...prevOrder,
      items: updatedItems,
    }));
    calculateTotal(updatedItems);
  };

  const calculateTotal = (items: any[]) => {
    const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    setNewOrder((prevOrder: any) => ({ ...prevOrder, total }));
  };

  const handleCreateOrder = async () => {
    try {
      const formattedOrder = {
        ...newOrder,
        items: newOrder.items.map((item: any) => ({
          ...item,
          variantId: item.variantId || null,
        })),
      };
      await createOrder(formattedOrder);
      setNewOrderModalOpen(false);
      fetchAllOrders();
    } catch (error) {
      console.error("Error creating order:", error);
    }
  };

  const toggleRowExpansion = (orderId: number) => {
    setExpandedOrderId(expandedOrderId === orderId ? null : orderId);
  };

  const navigateToOrderDetails = (orderId: number) => {
    window.location.href = `/dashboard/orders/${orderId}`;
  };

  const getApprovalBadge = (status: string) => {
    const normalizedStatus = status.toLowerCase().replace(/\s+/g, "_");
    const statusMap: { [key: string]: { label: string; color: string } } = {
      pending_approval: { label: "Pending Approval", color: "#FFC107" },
      approved: { label: "Approved", color: "#4CAF50" },
      rejected: { label: "Rejected", color: "#F44336" },
      n_a: { label: "N/A", color: "#9E9E9E" },
    };
    const { label, color } = statusMap[normalizedStatus] || statusMap["n_a"];
    return <Chip label={label} style={{ backgroundColor: color, color: "#FFF", fontWeight: "bold" }} />;
  };

  const getFulfillmentBadge = (status: string) => {
    const statusMap = {
      unfulfilled: { 
        color: "error" as const, 
        label: "Unfulfilled", 
        icon: <LocalShippingIcon /> 
      },
      partially_fulfilled: { 
        color: "warning" as const, 
        label: "Partially Fulfilled", 
        icon: <LocalShippingIcon /> 
      },
      fulfilled: { 
        color: "success" as const, 
        label: "Fulfilled", 
        icon: <CheckCircleIcon /> 
      }
    };
    
    const normalizedStatus = status.toLowerCase().replace(/\s+/g, "_");
    const { color, label, icon } = statusMap[normalizedStatus as keyof typeof statusMap] || statusMap.unfulfilled;
    
    return (
      <Chip
        icon={icon}
        label={label}
        color={color}
        sx={{ ml: 1 }}
      />
    );
  };

  const sendReminder = (orderId: number) => {
    axios.post(`https://gg-api.nationtonation.ca/api/orders/${orderId}/remind-payment`, {}, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
    })
    .then(() => alert(`Reminder sent for Order #${orderId}`))
    .catch(err => alert("Failed to send reminder"));
  };

  return (
    <Container>
      <Typography variant="h4" sx={{ marginBottom: 2 }}>
        Orders Management
      </Typography>
      {/* <Button
        variant="contained"
        color="primary"
        onClick={() => setNewOrderModalOpen(true)}
        sx={{ marginBottom: 2 }}
      >
        Create New Order
      </Button> */}

      {!isMobile ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Approval Status</TableCell>
                <TableCell>Fulfillment Status</TableCell>
                <TableCell>Total Amount</TableCell>
                <TableCell>Created Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <React.Fragment key={order.id}>
                  <TableRow hover onClick={() => navigateToOrderDetails(order.id)}>
                    <TableCell>{order.id}</TableCell>
<TableCell>
  {getCustomerName(order.customer_id)}
  {order.master_customer_name && (
    <Typography variant="caption" color="textSecondary" display="block">
      Master: {order.master_customer_name}
    </Typography>
  )}
</TableCell>
                    <TableCell>{order.status || "Pending"}</TableCell>
                    <TableCell>
                      {order.order_status ? getApprovalBadge(order.order_status) : getApprovalBadge("N/A")}
                    </TableCell>
                    <TableCell>
                      {getFulfillmentBadge(order.fulfillment_status || "unfulfilled")}
                    </TableCell>
                    <TableCell>${Number(order.total || 0).toFixed(2)}</TableCell>
                    <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {["pending", "partially_paid"].includes(order.status.toLowerCase()) && (
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              sendReminder(order.id);
                            }}
                          >
                            Remind
                          </Button>
                        )}
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRowExpansion(order.id);
                          }}
                        >
                          {expandedOrderId === order.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={8} style={{ padding: 0 }}>
                      <Collapse in={expandedOrderId === order.id} timeout="auto" unmountOnExit>
                        <Card sx={{ marginTop: 1, padding: 2 }}>
                          <CardContent>
                            <Typography variant="h6">Products</Typography>
                            {order.items.length > 0 ? (
                              order.items.map((item: any) => {
                                const product = products.find((p: any) => p.product_id === item.product_id);
                                const variant = product?.variants?.find((v: any) => v.id === item.variant_id);
                                return (
                                  <Grid container spacing={2} key={item.id} sx={{ marginTop: 1, padding: 1 }}>
                                    <Grid size={{xs: 3}}>
                                      {product?.product_image ? (
                                        <img
                                          src={product.product_image}
                                          alt={product.name || "Product Image"}
                                          style={{ width: "100%", borderRadius: "8px", maxHeight: "80px", objectFit: "cover" }}
                                        />
                                      ) : (
                                        <Typography variant="body2" color="textSecondary">No Image</Typography>
                                      )}
                                    </Grid>
                                    <Grid size={{xs: 9}}>
                                      <Typography variant="subtitle1">{product?.name || `Product ID: ${item.product_id}`}</Typography>
                                      <Typography variant="body2">
                                        Variant: {variant?.variant_name || "N/A"}
                                      </Typography>
                                      <Typography variant="body2">
                                        Quantity: {item.quantity}
                                      </Typography>
                                      <Typography variant="body2">
                                        {`Price: $${Number(item.price || 0).toFixed(2)} ${
                                          item.price === variant?.custom_price ? "(Custom)" : "(Base)"
                                        }`}
                                      </Typography>
                                      <Typography variant="body2">
                                        Subtotal: ${Number((item.price || 0) * (item.quantity || 1)).toFixed(2)}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                );
                              })
                            ) : (
                              <Typography color="error">No items found for this order.</Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Grid container spacing={2}>
          {orders.map((order) => (
            <Grid size={{xs: 12}} key={order.id}>
              <Card
                onClick={() => navigateToOrderDetails(order.id)}
                sx={{
                  padding: 2,
                  backgroundColor: "background.main",
                  borderRadius: 2,
                  boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <CardContent>
                  <Grid container alignItems="center" justifyContent="space-between">
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                      #{order.id}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {new Date(order.created_at).toLocaleDateString()}
                    </Typography>
                  </Grid>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
  <strong>Customer:</strong> {getCustomerName(order.customer_id)}
  {order.master_customer_name && (
    <Typography variant="caption" color="textSecondary" display="block">
      Master: {order.master_customer_name}
    </Typography>
  )}
</Typography>

                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid size={{xs: 6}}>
                      <Typography variant="body2">
                        <strong>Payment Status:</strong>
                      </Typography>
                    </Grid>
                    <Grid size={{xs: 6}}>{order.status || "Pending"}</Grid>
                  </Grid>
                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid size={{xs: 6}}>
                      <Typography variant="body2">
                        <strong>Approval Status:</strong>
                      </Typography>
                    </Grid>
                    <Grid size={{xs: 6}}>
                      {order.order_status
                        ? getApprovalBadge(order.order_status)
                        : getApprovalBadge("N/A")}
                    </Grid>
                  </Grid>
                  <Grid container spacing={1} sx={{ mt: 1 }}>
                    <Grid size={{xs: 6}}>
                      <Typography variant="body2">
                        <strong>Fulfillment Status:</strong>
                      </Typography>
                    </Grid>
                    <Grid size={{xs: 6}}>
                      {getFulfillmentBadge(order.fulfillment_status || "unfulfilled")}
                    </Grid>
                  </Grid>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    <strong>Total:</strong> ${Number(order.total || 0).toFixed(2)}
                  </Typography>
                  {["pending", "partially_paid"].includes(order.status.toLowerCase()) && (
                    <Button
                      fullWidth
                      variant="outlined"
                      color="warning"
                      sx={{ mt: 2 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        sendReminder(order.id);
                      }}
                    >
                      Remind
                    </Button>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>      
      )}

      {/* <Dialog
        open={newOrderModalOpen}
        onClose={() => setNewOrderModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Order</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Customer</InputLabel>
            <Select
              value={newOrder.customerId}
              onChange={(e) => handleCustomerChange(Number(e.target.value))}
            >
              {customers.map((customer: any) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.name} - {customer.email}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {newOrder.items.map((item: any, index: number) => (
            <Grid container spacing={2} key={index}>
              <Grid size={{xs: 3}}>
                <FormControl fullWidth>
                  <InputLabel>Product</InputLabel>
                  <Select
                    value={item.productId || ""}
                    onChange={(e) => handleItemChange(index, "productId", e.target.value)}
                  >
                    {products.map((product: any) => (
                      <MenuItem key={product.product_id} value={product.product_id}>
                        {product.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{xs: 3}}>
                <FormControl fullWidth>
                  <InputLabel>Variant</InputLabel>
                  <Select
                    value={item.variantId || ""}
                    onChange={(e) => handleItemChange(index, "variantId", e.target.value)}
                    disabled={!item.productId}
                  >
                    {products
                      .find((product: any) => product.product_id === item.productId)
                      ?.variants.length > 0 ? (
                        products
                          .find((product: any) => product.product_id === item.productId)
                          ?.variants.map((variant: any) => (
                            <MenuItem key={variant.id} value={variant.id}>
                              {variant.variant_name}
                            </MenuItem>
                          ))
                      ) : (
                        <MenuItem disabled>No Variants Available</MenuItem>
                      )}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{xs: 2}}>
                <TextField
                  label="Quantity"
                  type="number"
                  value={item.quantity || 1}
                  onChange={(e) => handleItemChange(index, "quantity", parseInt(e.target.value, 10))}
                  fullWidth
                />
              </Grid>
              <Grid size={{xs: 2}}>
                <TextField
                  label="Price"
                  type="number"
                  value={item.price}
                  onChange={(e) => handleItemChange(index, "price", parseFloat(e.target.value))}
                  fullWidth
                />
              </Grid>
              <Grid size={{xs: 2}}>
                <IconButton onClick={() => handleRemoveItem(index)}>
                  <RemoveIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button variant="text" startIcon={<AddIcon />} onClick={handleAddItem}>
            Add Item
          </Button>
          <Typography variant="h6" sx={{ marginTop: 2 }}>
            Total: ${newOrder.total.toFixed(2)}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOrderModalOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleCreateOrder}>
            Create Order
          </Button>
        </DialogActions>
      </Dialog> */}
    </Container>
  );
};

export default OrdersPage;